// src/oura/ask/planProposals.ts
//
// Ask Oura (PREVIEW): "approved answers become proposals". The answers the
// client agreed in the Answer Plan (agreed.ts, read from the doc's own
// threads) are shown on the page they belong to as ordinary copy proposals,
// marked "From the Answer Plan · approved by <client>". Every answer agreed
// so far for the sample client belongs on the homepage.
//
// SAMPLE ONLY. No real mapping exists yet: nothing links an Answer Plan thread
// to a block of a client's parsed page (no BE field), so the "Now" text here is
// the sample plan's own question, not text read from any live page. The
// sample's page blocks are sample content (nyonyaSamplePage). Callers pass
// `sample: true` only for the sample business on the dev server. A real client
// gets null here and the chat falls back to "could not match the wording".

import { dayMonth } from "../visibility/format";
import type { CopyRecommendation, PageBlock } from "../pageWorkshop";
import type { AgreedAnswer, AgreedSet } from "./agreed";

/** The page the sample's agreed answers map to. */
export const PLAN_PAGE_PATH = "/";
export const PLAN_PAGE_TITLE = "Homepage";

export const planPageUrl = (domain: string): string => `https://${domain.replace(/^https?:\/\//, "").replace(/\/+$/, "")}/`;

export function isPlanPage(url: string): boolean {
    try {
        return (new URL(url, "https://sample.invalid").pathname.replace(/\/+$/, "") || "/") === PLAN_PAGE_PATH;
    } catch {
        return false;
    }
}

export const originLabel = (a: AgreedSet): string => `From the ${a.docTitle} · approved by ${a.clientName}`;

interface Mapped {
    blocks: PageBlock[];
    rec: Omit<CopyRecommendation, "origin">;
}

function mapAnswer(x: AgreedAnswer, docTitle: string): Mapped {
    const id = `plan-${x.threadId}`;
    const said = `${x.by} answered in the ${docTitle} on ${dayMonth(x.at)}: “${x.answer}”`;
    // The proposal says the approved answer, verbatim: no copy of our own, so it can never contradict what the client agreed.
    const before = x.question;
    return {
        blocks: [{ id: `${id}-p`, tag: "p", text: before }],
        rec: {
            kind: "copy",
            id,
            blockId: `${id}-p`,
            before,
            after: x.answer,
            justification: { reason: said, expected: "The page says what the client approved." },
        },
    };
}

/**
 * The plan page's extra blocks and proposals, or null for any other page and
 * for any business that is not the sample (`sample` is false for real clients).
 */
export function planProposals(agreed: AgreedSet | null, url: string, sample: boolean): { blocks: PageBlock[]; recs: CopyRecommendation[] } | null {
    if (!sample || !agreed || !isPlanPage(url)) return null;
    const label = originLabel(agreed);
    const mapped = agreed.answers.map((a) => mapAnswer(a, agreed.docTitle));
    return {
        blocks: mapped.flatMap((m) => m.blocks),
        recs: mapped.map((m) => ({ ...m.rec, origin: label })),
    };
}
